import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import {
  getDocuments,
  uploadDocument,
  parseDocument,
  deleteDocument,
  downloadDocument,
  getDocumentStats,
  formatFileSize,
  formatDate,
  type Document,
  type DocumentStats,
} from '../api/documents';

export function Documents() {
  const navigate = useNavigate();

  const [documents, setDocuments] = useState<Document[]>([]);
  const [stats, setStats] = useState<DocumentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [filter, setFilter] = useState<'all' | 'parsed' | 'unparsed'>('all');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [docsRes, statsRes] = await Promise.all([
        getDocuments({
          parsed: filter === 'parsed' ? true : filter === 'unparsed' ? false : undefined,
        }),
        getDocumentStats(),
      ]);
      setDocuments(docsRes.documents);
      setStats(statsRes.stats);
    } catch (error) {
      console.error('Error loading documents:', error);
      setMessage({ type: 'error', text: 'Errore nel caricamento dei documenti' });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    setMessage(null);

    try {
      for (const file of Array.from(files)) {
        await uploadDocument(file);
      }
      setMessage({ type: 'success', text: `${files.length} file caricato/i con successo` });
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Errore durante il caricamento' });
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleParse = async (doc: Document) => {
    setParsing(doc.id);
    setMessage(null);

    try {
      const result = await parseDocument(doc.id);
      setMessage({
        type: 'success',
        text: `Analisi completata: ${result.result.parametersFound} parametri trovati`,
      });
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Errore durante l\'analisi' });
    } finally {
      setParsing(null);
    }
  };

  const handleDelete = async (doc: Document) => {
    if (!confirm(`Eliminare "${doc.originalFilename}"?`)) return;

    try {
      await deleteDocument(doc.id);
      setMessage({ type: 'success', text: 'Documento eliminato' });
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Errore durante l\'eliminazione' });
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      await downloadDocument(doc.id, doc.originalFilename);
    } catch (error) {
      setMessage({ type: 'error', text: 'Errore durante il download' });
    }
  };

  // Selection handlers
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === documents.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(documents.map((d) => d.id)));
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  // Bulk actions
  const handleBulkParse = async () => {
    const toParse = documents.filter(
      (d) => selectedIds.has(d.id) && !d.parsed && (d.fileType === 'pdf' || d.fileType === 'image')
    );
    if (toParse.length === 0) {
      setMessage({ type: 'error', text: 'Nessun documento da analizzare selezionato' });
      return;
    }

    setBulkProcessing(true);
    setMessage(null);
    let success = 0;
    let failed = 0;

    for (const doc of toParse) {
      try {
        await parseDocument(doc.id);
        success++;
      } catch {
        failed++;
      }
    }

    setBulkProcessing(false);
    setMessage({
      type: success > 0 ? 'success' : 'error',
      text: `Analisi completata: ${success} riusciti, ${failed} falliti`,
    });
    clearSelection();
    loadData();
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Eliminare ${selectedIds.size} documenti selezionati?`)) return;

    setBulkProcessing(true);
    setMessage(null);
    let success = 0;
    let failed = 0;

    for (const id of selectedIds) {
      try {
        await deleteDocument(id);
        success++;
      } catch {
        failed++;
      }
    }

    setBulkProcessing(false);
    setMessage({
      type: success > 0 ? 'success' : 'error',
      text: `Eliminazione completata: ${success} riusciti, ${failed} falliti`,
    });
    clearSelection();
    loadData();
  };

  const handleBulkDownload = async () => {
    const toDownload = documents.filter((d) => selectedIds.has(d.id));
    if (toDownload.length === 0) return;

    setBulkProcessing(true);
    for (const doc of toDownload) {
      try {
        await downloadDocument(doc.id, doc.originalFilename);
        await new Promise((r) => setTimeout(r, 500));
      } catch {
        // Continue with other downloads
      }
    }
    setBulkProcessing(false);
    setMessage({ type: 'success', text: `Download di ${toDownload.length} file avviato` });
  };

  if (loading) {
    return (
      <Layout title="Documenti e Referti">
        <div className="flex items-center justify-center h-64">
          <div className="text-xl text-gray-500">Caricamento...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Documenti e Referti">
      <div className="max-w-7xl mx-auto">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8 lg:mb-12">
            <div className="bg-blue-50 rounded-2xl shadow-sm p-5 lg:p-6">
              <p className="text-3xl lg:text-4xl font-bold text-blue-600">{stats.totalDocuments}</p>
              <p className="text-base lg:text-lg text-gray-600 mt-1">Documenti totali</p>
            </div>
            <div className="bg-green-50 rounded-2xl shadow-sm p-5 lg:p-6">
              <p className="text-3xl lg:text-4xl font-bold text-green-600">{stats.parsedDocuments}</p>
              <p className="text-base lg:text-lg text-gray-600 mt-1">Analizzati</p>
            </div>
            <div className="bg-purple-50 rounded-2xl shadow-sm p-5 lg:p-6">
              <p className="text-3xl lg:text-4xl font-bold text-purple-600">{stats.verifiedDocuments}</p>
              <p className="text-base lg:text-lg text-gray-600 mt-1">Verificati</p>
            </div>
            <div className="bg-orange-50 rounded-2xl shadow-sm p-5 lg:p-6">
              <p className="text-3xl lg:text-4xl font-bold text-orange-600">{stats.totalParameters}</p>
              <p className="text-base lg:text-lg text-gray-600 mt-1">Parametri estratti</p>
            </div>
          </div>
        )}

        {/* Message */}
        {message && (
          <div
            className={`mb-6 p-4 lg:p-5 rounded-xl text-base lg:text-lg ${
              message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Upload area */}
        <div
          className={`mb-8 lg:mb-12 border-2 border-dashed rounded-2xl p-8 lg:p-12 text-center transition-colors ${
            dragOver
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="text-5xl lg:text-6xl mb-4">
            {uploading ? '...' : '📄'}
          </div>
          <p className="text-lg lg:text-xl text-gray-600 mb-2">
            {uploading ? 'Caricamento in corso...' : 'Trascina qui i tuoi referti PDF'}
          </p>
          <p className="text-base text-gray-400 mb-6">oppure</p>
          <label className="cursor-pointer">
            <span className="px-6 py-3 bg-blue-600 text-white text-base lg:text-lg font-medium rounded-xl hover:bg-blue-700 transition-colors">
              Seleziona file
            </span>
            <input
              type="file"
              className="hidden"
              accept=".pdf,image/*"
              multiple
              onChange={(e) => handleFileSelect(e.target.files)}
              disabled={uploading}
            />
          </label>
          <p className="text-sm lg:text-base text-gray-400 mt-6">
            Formati supportati: PDF, JPEG, PNG (max 20MB)
          </p>
        </div>

        {/* Filters and Bulk Actions */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 lg:mb-8">
          <div className="flex items-center gap-4">
            <span className="text-base lg:text-lg text-gray-600">Filtra:</span>
            <div className="flex gap-2">
              {(['all', 'parsed', 'unparsed'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 text-base lg:text-lg rounded-xl transition-colors ${
                    filter === f
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f === 'all' ? 'Tutti' : f === 'parsed' ? 'Analizzati' : 'Da analizzare'}
                </button>
              ))}
            </div>
          </div>

          {/* Bulk Actions Toolbar */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 bg-gray-50 px-5 py-3 rounded-xl border border-gray-200">
              <span className="text-base lg:text-lg text-gray-700 font-medium">
                {selectedIds.size} selezionati
              </span>
              <div className="flex gap-2">
                <button
                  onClick={handleBulkParse}
                  disabled={bulkProcessing}
                  className="px-4 py-2 text-base bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50"
                  title="Analizza selezionati"
                >
                  {bulkProcessing ? '...' : 'Analizza'}
                </button>
                <button
                  onClick={handleBulkDownload}
                  disabled={bulkProcessing}
                  className="px-4 py-2 text-base bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 disabled:opacity-50"
                  title="Scarica selezionati"
                >
                  Scarica
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkProcessing}
                  className="px-4 py-2 text-base bg-gray-100 text-red-600 rounded-xl hover:bg-red-50 disabled:opacity-50"
                  title="Elimina selezionati"
                >
                  Elimina
                </button>
                <button
                  onClick={clearSelection}
                  className="px-3 py-2 text-base text-gray-400 hover:text-gray-600"
                  title="Deseleziona tutto"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Documents list */}
        {documents.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-8 lg:p-12 text-center">
            <p className="text-xl text-gray-500">Nessun documento caricato</p>
            <p className="text-base lg:text-lg text-gray-400 mt-2">
              Carica i tuoi referti per iniziare ad analizzare i dati sanitari
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '900px' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 lg:px-6 py-4">
                    <input
                      type="checkbox"
                      checked={documents.length > 0 && selectedIds.size === documents.length}
                      onChange={toggleSelectAll}
                      className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-4 lg:px-6 py-4 text-left text-sm lg:text-base font-semibold text-gray-600 uppercase tracking-wider">
                    Azioni
                  </th>
                  <th className="px-4 lg:px-6 py-4 text-left text-sm lg:text-base font-semibold text-gray-600 uppercase tracking-wider">
                    Stato
                  </th>
                  <th className="px-4 lg:px-6 py-4 text-left text-sm lg:text-base font-semibold text-gray-600 uppercase tracking-wider">
                    Documento
                  </th>
                  <th className="px-4 lg:px-6 py-4 text-left text-sm lg:text-base font-semibold text-gray-600 uppercase tracking-wider">
                    Data Referto
                  </th>
                  <th className="px-4 lg:px-6 py-4 text-left text-sm lg:text-base font-semibold text-gray-600 uppercase tracking-wider">
                    Laboratorio
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documents.map((doc) => (
                  <tr
                    key={doc.id}
                    className={`hover:bg-gray-50 ${selectedIds.has(doc.id) ? 'bg-blue-50' : ''}`}
                  >
                    <td className="px-4 lg:px-6 py-4 lg:py-5 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(doc.id)}
                        onChange={() => toggleSelect(doc.id)}
                        className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 lg:px-6 py-4 lg:py-5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {!doc.parsed && (doc.fileType === 'pdf' || doc.fileType === 'image') && (
                          <button
                            onClick={() => handleParse(doc)}
                            disabled={parsing === doc.id}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg disabled:opacity-50"
                            title="Analizza"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </button>
                        )}
                        {doc.parsed && (
                          <button
                            onClick={() => navigate(`/parameters`)}
                            className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg"
                            title="Visualizza parametri"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => handleDownload(doc)}
                          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                          title="Scarica"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(doc)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg"
                          title="Elimina"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4 lg:py-5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {doc.parsed ? (
                          <span className="px-3 py-1 text-sm lg:text-base rounded-full bg-green-100 text-green-800 font-medium">
                            Analizzato
                          </span>
                        ) : (
                          <span className="px-3 py-1 text-sm lg:text-base rounded-full bg-yellow-100 text-yellow-800 font-medium">
                            Da analizzare
                          </span>
                        )}
                        {doc.parsingConfidence !== null && doc.parsingConfidence > 0 && (
                          <span className="text-sm lg:text-base text-gray-500">
                            {Math.round(doc.parsingConfidence * 100)}%
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4 lg:py-5 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-3xl mr-4">
                          {doc.fileType === 'pdf' ? '📄' : '🖼️'}
                        </span>
                        <div>
                          <div className="text-base lg:text-lg font-medium text-gray-900">
                            {doc.originalFilename}
                          </div>
                          <div className="text-sm lg:text-base text-gray-500">
                            {formatFileSize(doc.fileSize)} • Caricato il {formatDate(doc.uploadedAt)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4 lg:py-5 whitespace-nowrap text-base lg:text-lg text-gray-600">
                      {doc.documentDate ? formatDate(doc.documentDate) : '-'}
                    </td>
                    <td className="px-4 lg:px-6 py-4 lg:py-5 whitespace-nowrap text-base lg:text-lg text-gray-600">
                      {doc.labName || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
